'use client';
import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { Callout as CalloutType } from '@lib/types/cms/Callout';
import { toNonNullable } from '@lib/util/toNonNullable';
import { ButtonStyle, Callout as CalloutUI } from 'ui-components';

type CalloutProps = {
  data: CalloutType;
  position: number;
};

const Callout = ({ data, position }: CalloutProps) => {
  const {
    alignment,
    heading,
    description,
    image,
    ctas,
    imageFocusArea,
    trackAsPromo,
    titleInternal,
  } = data || {};
  const align = alignment === 'Left' ? 'left' : 'right';
  const imageUrl = image?.url;
  const imageDescription = image?.alt;

  const actions = ctas.map((cta) => ({
    label: cta.label,
    url: cta.url,
    openInNewTab: toNonNullable(cta.openInNewTab),
    style:
      cta.appearance === 'primary'
        ? ('primary' as ButtonStyle)
        : ('secondary' as ButtonStyle),
  }));

  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: titleInternal || '',
      typename: 'Callout',
      position,
      creativeTitle: toNonNullable(image?.title),
      trackAsPromo: trackAsPromo ?? false,
      ctas: actions.map((action) => action.label),
    });

  if (!heading || !imageUrl) {
    return null;
  }

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={trackAsPromo ?? false}
    >
      <CalloutUI
        title={heading}
        image={{
          url: imageUrl,
          alt: imageDescription ?? '',
          height: toNonNullable(image?.height),
          width: toNonNullable(image?.width),
        }}
        imageFocusArea={imageFocusArea}
        description={description}
        actions={actions}
        align={align}
        onCtaClick={promoClickTrackingEvent}
      />
    </TrackPromotionImpression>
  );
};

export default Callout;
